import Rate from 'rc-rate';
import React from 'react';
import { Helmet } from 'react-helmet';
import Imgix from 'react-imgix';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import useMedia from '../../../../hooks/useMedia';
import { StarStyles } from '../../../../styles/theme/default/others';
import { getImgixUrl } from '../../../../utils/helper';

const Wrapper = styled.div`
  background: ${({ theme }) => theme.old ? theme.colors.oatmilk : theme.colors.mist};
  text-align: left;
  margin-bottom: ${({ minimalist }) => (minimalist ? '10px' : '50px')};

  .container {
    max-width: 1400px !important;
  }

  .text-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 64px 64px 64px 15px;

    h1 {
      text-align: left;
      font-family: ${({ theme }) => theme.fonts.secondary.family};
      letter-spacing: ${({ theme }) => theme.fonts.secondary.letterSpacing};
      font-style: normal;
      font-weight: ${({ theme }) => theme.old ? 'normal' : '300'};
      font-size: 45px;
      line-height: 55px;
      margin-bottom: 0;
    }
    p {
      margin-top: 16px;
      font-family: ${({ theme }) => theme.fonts.primary};
      font-style: normal;
      font-weight: normal;
      font-size: 18px;
      line-height: 25px;
      margin-bottom: 0;
    }
    .take-quiz {
      margin-top: 20px;
      text-align: center;
      a {
        transition: all 0.1s;
        color: ${({ theme }) => theme.colors.charcoal};
        &:hover {
          text-decoration: none;
          color: #245caf;
        }
      }
      a span {
        padding-bottom: 3px;
        border-bottom: 2px solid black;
        transition: all 0.1s;
        &:hover {
          border-bottom: 2px solid #245caf;
        }
      }
    }
    &.one-column {
      max-width: ${({ minimalist }) => (minimalist ? '1300px' : '750px')};
      margin-left: auto;
      margin-right: auto;
      /* padding-left: 15px;
      padding-right: 15px; */
      padding: ${({ minimalist }) => (minimalist ? '50px 15px' : 'auto 15px')};
      h1,
      p {
        text-align: ${({ minimalist }) => (minimalist ? 'start' : 'center')};
      }
    }
  }
  .image-wrapper {
    display: flex;
    align-items: center;
  }

  @media (max-width: 992px) {
    margin-bottom: 0;
    .container {
      max-width: 100%;
      .row-wrapper {
        .text-wrapper {
          padding: 30px 15px;
          h1 {
            font-size: 30px;
            line-height: 40px;
          }
          p {
            max-width: 600px;
            margin: 16px auto 0 auto;
            font-size: 16px;
            line-height: 23px;
          }
          h1,
          p {
            text-align: center;
          }
        }
        .image-wrapper {
        }
      }
    }
  }
`;

const InstagramHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  font-size: 13px;
  cursor: pointer;
  padding: 5px;
  background: rgba(255, 255, 255, 0.7);
  a {
    color: ${({ theme }) => theme.colors.charcoal};
    text-decoration: none;
  }
  @media (max-width: 992px) {
    width: 100%;
    text-align: center;
  }
`;

const DarkButtonLink = styled.a`
  margin-top: 30px;
  width: 200px;
  border: 1px solid black;
  background: black;
  padding: 15px 5px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  text-decoration: none;
  line-height: 19px;
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: 0.04em;
  display: flex;
  justify-content: center;
  color: white;

  transition: all 0.3s;
  &:hover {
    color: white;
    text-decoration: none;
    background: #4d4d4d;
  }
`;

function SectionHeader({
  title,
  rating,
  description,
  image,
  instagramHandle,
  showQuizLink,
  quizLink,
  ctaName,
  ctaUrl,
  className,
  seoTitle,
  seoDescription,
  minimalist
}) {
  const isMobile =
    typeof window !== `undefined` ? useMedia('(max-width: 992px) ') : null;

  const addLineBreaks = string => {
    return string.split(/(?:\r\n|\r|\n)/g).map((text, index) => (
      <React.Fragment key={index}>
        {text}
        <br />
      </React.Fragment>
    ));
  };

  const textModule = () => {
    return (
      <div
        className={`${isMobile ? 'col-12' : 'col'} text-wrapper ${
          image ? 'two-column' : 'one-column'
        }`}
      >
        <h1>{title}</h1>

        {rating && (
          <div className="text-center">
            <StarStyles>
              <Rate
                value={parseFloat(rating)}
                style={{ fontSize: 16, marginTop: 5 }}
                allowClear={false}
                disabled={true}
                allowHalf
                character={<i className="icon-star-rating" />}
              />
            </StarStyles>
          </div>
        )}
        {description && <p>{addLineBreaks(description)}</p>}

        {ctaName && ctaUrl && (
          <DarkButtonLink className="mx-auto" target="_blank" href={ctaUrl}>
            {ctaName}
          </DarkButtonLink>
        )}

        {showQuizLink && (
          <div className="take-quiz">
            <Link to={quizLink} className="link">
              <span>Take our quiz for personalized results</span> {'>'}
            </Link>
          </div>
        )}
      </div>
    );
  };

  const imageModule = () => {
    return (
      <div className={`${isMobile ? 'col-12' : 'col'} p-0 image-wrapper`}>
        <div className="position-relative w-100">
          <Imgix
            imgixParams={{
              auto: 'compress,format'
            }}
            className="w-100"
            sizes="(min-width: 992px) 50vw,
            100vw"
            src={getImgixUrl(image)}
            htmlAttributes={{
              alt: title
            }}
          />
          {instagramHandle && (
            <InstagramHandle>
              <a
                href={`https://www.instagram.com/${instagramHandle}/`}
                target="_blank"
                rel="noopener"
                aria-label="Instagram"
              >
                @{instagramHandle}
              </a>
            </InstagramHandle>
          )}
        </div>
      </div>
    );
  };

  return (
    <Wrapper className={className ? className : ''} minimalist={minimalist}>
      <Helmet>
        {seoTitle && <title>{seoTitle}</title>}
        {seoDescription && <meta name="description" content={seoDescription} />}
      </Helmet>
      <div className="container mb-0">
        <div className="row row-wrapper">
          {isMobile ? (
            <>
              {image && imageModule()}
              {textModule()}
            </>
          ) : (
            <>
              {textModule()}
              {image && imageModule()}
            </>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

export default SectionHeader;
